import React, { ChangeEvent } from 'react';
import { Box, Checkbox, Paper, Stack, Typography } from '@mui/material';
import { BORDER, INK, RADIUS, SURFACE } from './notificationTokens';
import {
  NotificationUniversalSettingDefinitionType,
  NotificationUniversalSettingType,
} from '../../util/NotificationUtil';

interface AccountWideSettingsProps {
  settingDefinitions: readonly NotificationUniversalSettingDefinitionType[];
  values: NotificationUniversalSettingType | undefined | null;
  onToggle: (settingName: string, value: boolean) => void;
}

/**
 * Account-wide toggles that apply to every role. Rendered as standalone setting
 * rows (NOT inside the matrix table). Each row gains an accent border when on.
 * Not affected by the Account Owner fallback rule.
 *
 * The description text is the existing source tooltip copy for the setting —
 * surfaced inline here rather than behind an info icon.
 */
const AccountWideSettings = ({ settingDefinitions, values, onToggle }: AccountWideSettingsProps) => {
  if (!settingDefinitions.length) return null;

  const handleChange = (propertyName: string) => (e: ChangeEvent<HTMLInputElement>) => {
    onToggle(propertyName, e.target.checked);
  };

  return (
    <Stack spacing={1.5} sx={{ mb: 3 }} aria-label="Account-wide settings">
      {settingDefinitions.map(({ propertyName, label, tooltip }) => {
        const on = !!values?.[propertyName];
        return (
          <Paper
            key={propertyName}
            variant="outlined"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              p: 2,
              borderRadius: RADIUS.panel,
              backgroundColor: on ? 'neutral.light' : SURFACE.cool,
              borderColor: on ? 'primary.main' : BORDER.subtle,
              transition: 'border-color 0.18s, background-color 0.18s',
            }}
          >
            <Checkbox
              checked={on}
              onChange={handleChange(propertyName)}
              inputProps={{ 'aria-label': label }}
              sx={{ p: 0, mt: '2px' }}
            />
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: INK.heading }}>{label}</Typography>
              <Typography sx={{ fontSize: 14, color: INK.secondary }}>{tooltip}</Typography>
            </Box>
          </Paper>
        );
      })}
    </Stack>
  );
};

export { AccountWideSettings };
